import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useParams } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { CookieConsentProvider } from './contexts/CookieConsentContext';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { CartDrawer } from './components/CartDrawer';
import { CookieBanner } from './components/CookieBanner';
import { CookieSettingsModal } from './components/CookieSettingsModal';
import { ConditionalScripts } from './components/ConditionalScripts';
import { Home } from './pages/Home';
import { Shop } from './pages/Shop';
import { ProductDetail } from './pages/ProductDetail';
import { Checkout } from './pages/Checkout';
import { OrderSuccess } from './pages/OrderSuccess';
import { Login } from './pages/Login';
import { About } from './pages/About';
import { Contact } from './pages/Contact';
import { FAQ } from './pages/FAQ';
import { Admin } from './pages/Admin';
import { ShippingPolicy } from './pages/ShippingPolicy';
import { ReturnsPolicy } from './pages/ReturnsPolicy';
import { SizingGuide } from './pages/SizingGuide';
import { PrivacyPolicy } from './pages/PrivacyPolicy';
import { TermsConditions } from './pages/TermsConditions';
import { ProductConditionPolicy } from './pages/ProductConditionPolicy';
import { AuthenticityPolicy } from './pages/AuthenticityPolicy';
import { OneOfAKindPolicy } from './pages/OneOfAKindPolicy';
import { OrderCancellationPolicy } from './pages/OrderCancellationPolicy';
import { PaymentPolicy } from './pages/PaymentPolicy';
import { CookiePolicy } from './pages/CookiePolicy';
import { TrackOrder } from './pages/TrackOrder';
import { Wishlist } from './pages/Wishlist';
import MyOrders from './pages/MyOrders';
import { WhatsAppButton } from './components/WhatsAppButton';
import { MobileBottomNav } from './components/MobileBottomNav';
import { ScrollToTop } from './components/ScrollToTop';
import { Toaster } from './components/ui/sonner';
import { HelmetProvider } from 'react-helmet-async';
// OpenClaw pages
import OpenClawLogin from './pages/LoginPage';
import OpenClawSetup from './pages/SetupPage';
import AuthCallback from './pages/AuthCallback';
import './App.css';

const AdminBlocked = () => (
  <div className="min-h-screen flex items-center justify-center" data-testid="admin-blocked">
    <div className="text-center"><h1 className="text-4xl font-heading font-semibold mb-4">404</h1><p className="text-[#666]">Page not found.</p></div>
  </div>
);

const DynamicAdminRoute = () => {
  const { '*': path } = useParams();
  if (path && path.startsWith('vwc-panel-')) {
    return <Admin />;
  }
  return <AdminBlocked />;
};

const CartRedirect = () => {
  const { openDrawer } = require('./contexts/CartContext').useCart();
  useEffect(() => { openDrawer(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]" data-testid="cart-page">
      <div className="text-center">
        <p className="text-[#666] text-sm">Opening your bag...</p>
      </div>
    </div>
  );
};

// OpenClaw wrapper - no Navbar/Footer, standalone dark theme
function OpenClawRoutes() {
  // Check URL fragment for session_id (Google Auth callback)
  const hash = window.location.hash;
  if (hash && hash.includes('session_id=')) {
    return (
      <div className="dark">
        <Toaster richColors position="top-center" />
        <AuthCallback />
      </div>
    );
  }
  
  return (
    <div className="dark">
      <Toaster richColors position="top-center" />
      <Routes>
        <Route path="login" element={<OpenClawLogin />} />
        <Route path="setup" element={<OpenClawSetup />} />
        <Route path="*" element={<OpenClawSetup />} />
      </Routes>
    </div>
  );
}

function App() {
  useEffect(() => {
    const id = 'emergent' + '-badge';
    const hide = () => {
      const el = document.getElementById(id);
      if (el) {
        el.style.setProperty('display', 'none', 'important');
        el.style.setProperty('visibility', 'hidden', 'important');
        el.style.setProperty('opacity', '0', 'important');
        el.style.setProperty('width', '0', 'important');
        el.style.setProperty('height', '0', 'important');
        el.style.setProperty('overflow', 'hidden', 'important');
        el.style.setProperty('pointer-events', 'none', 'important');
        el.style.setProperty('position', 'fixed', 'important');
        el.style.setProperty('top', '-9999px', 'important');
        el.style.setProperty('left', '-9999px', 'important');
        el.style.setProperty('clip-path', 'inset(100%)', 'important');
        el.style.setProperty('transform', 'scale(0)', 'important');
      }
    };
    hide();
    const raf = () => { hide(); requestAnimationFrame(raf); };
    requestAnimationFrame(raf);
    const obs = new MutationObserver(hide);
    obs.observe(document.documentElement, { childList: true, subtree: true, attributes: true });
    return () => obs.disconnect();
  }, []);

  return (
    <HelmetProvider>
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <CookieConsentProvider>
          <Routes>
            {/* OpenClaw routes - standalone, no e-commerce chrome */}
            <Route path="/openclaw/*" element={<OpenClawRoutes />} />
            
            {/* Auth callback for OpenClaw Google Auth */}
            <Route path="/openclaw-callback" element={<AuthCallback />} />
            
            {/* E-commerce routes */}
            <Route path="/*" element={
              <div className="App">
                <ScrollToTop />
                <Navbar />
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/shop" element={<Shop />} />
                  <Route path="/products/:id" element={<ProductDetail />} />
                  <Route path="/product/:id" element={<ProductDetail />} />
                  <Route path="/cart" element={<CartRedirect />} />
                  <Route path="/checkout" element={<Checkout />} />
                  <Route path="/order-success" element={<OrderSuccess />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/faq" element={<FAQ />} />
                  <Route path="/admin" element={<AdminBlocked />} />
                  <Route path="/shipping-policy" element={<ShippingPolicy />} />
                  <Route path="/returns-policy" element={<ReturnsPolicy />} />
                  <Route path="/sizing-guide" element={<SizingGuide />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/terms-conditions" element={<TermsConditions />} />
                  <Route path="/product-condition" element={<ProductConditionPolicy />} />
                  <Route path="/authenticity" element={<AuthenticityPolicy />} />
                  <Route path="/one-of-a-kind" element={<OneOfAKindPolicy />} />
                  <Route path="/order-cancellation" element={<OrderCancellationPolicy />} />
                  <Route path="/payment-policy" element={<PaymentPolicy />} />
                  <Route path="/cookie-policy" element={<CookiePolicy />} />
                  <Route path="/track-order" element={<TrackOrder />} />
                  <Route path="/wishlist" element={<Wishlist />} />
                  <Route path="/my-orders" element={<MyOrders />} />
                  <Route path="/*" element={<DynamicAdminRoute />} />
                </Routes>
                <Footer />
                <CartDrawer />
                <WhatsAppButton />
                <MobileBottomNav />
                <CookieBanner />
                <CookieSettingsModal />
                <ConditionalScripts />
                <Toaster />
              </div>
            } />
          </Routes>
          </CookieConsentProvider>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
